import { computed, MaybeRef, unref } from 'vue';
import { CODES, useLiveQueryV2 } from '@libs/use';
import {
  PositionItem,
  Positions,
  UnderlyingAssetIsin,
} from '@libs/api-reference';
import {
  SYMBOL_TRADE_CHANNEL_PREFIX,
  SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX,
  transformTradePriceChange,
  transformSymbolTradingStateChange,
  TransformTradePriceChangeDataType,
  TransformSymbolTradingStateChangeDataType,
  useSymbolDeductions,
} from '@core/symbol';
import { calculateOpenPositionReturnValuePersentage } from '@core/portfolio';

import { getPositionSymbolValues } from '../utils/calculator/position';

import * as API from '../requests';

import { OPTION_QUERY_KEYS, SETTLEMENT_CHANNEL } from '../constants';
import { useUser } from '@core/user';
import { createPositionChannel } from '../utils/socket';
import {
  transformCashSettlementChanged,
  transformPhysicalSettlementChanged,
  transformPositionChanged,
  transformPositionCreated,
  transformPositionRemoved,
} from '../utils/transform/position';

type TOptionPositions = (PositionItem &
  ReturnType<typeof getPositionSymbolValues>)[];

type TUseLiveOptionPositionsQuery = {
  immediate?: MaybeRef<boolean>;
  shouldRefetch?: boolean;
};

export default function useLiveOptionPositionsQuery(
  { immediate, shouldRefetch }: TUseLiveOptionPositionsQuery = {
    immediate: true,
    shouldRefetch: false,
  }
) {
  const { findWageConfigBySymbol } = useSymbolDeductions();
  const { user, isUserReady } = useUser();

  const {
    result,
    status: getOptionPositionsStatus,
    execute: getOptionPositions,
    isPending: isLoadingOptionPositions,
    isSuccess: isSuccessLoadingOptionPositions,
    isError: isErrorLoadingOptionPositions,
  } = useLiveQueryV2<Positions>({
    fetcher: () => API.getOptionPositions({ pageNumber: 1, pageSize: 100 }),
    key: OPTION_QUERY_KEYS.positions,
    refetchInterval: shouldRefetch ? 10000 : false,
    unsubscribe: false,
    immediate: computed(() => Boolean(isUserReady.value && unref(immediate))),
    getChannels(result) {
      const symbols = result?.data.items || [];
      const positionChannel = createPositionChannel(user.value!.bimCode);

      return symbols?.reduce(
        (symbolsChannels: string[], currentSymbol) => {
          symbolsChannels.push(
            `${SYMBOL_TRADE_CHANNEL_PREFIX}${currentSymbol.isin}`,
            `${SYMBOL_TRADE_CHANNEL_PREFIX}${currentSymbol.underlyingAssetIsin}`,
            `${SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX}${currentSymbol.isin}`
          );

          return symbolsChannels;
        },
        [positionChannel, SETTLEMENT_CHANNEL]
      );
    },
    async onUpdate(signal) {
      const actions = {
        [CODES.SymbolTradePriceChangeSignal]: transformTradePriceChange,
        [CODES.SymbolStateChangeSignal]: transformSymbolTradingStateChange,
        [CODES.OptionPositionCreated]: transformPositionCreated,
        [CODES.OptionPositionChanged]: transformPositionChanged,
        [CODES.OptionPositionRemoved]: transformPositionRemoved,
        [CODES.CashSettlementRequestGatheringStarted]:
          transformCashSettlementChanged,
        [CODES.PhysicalSettlementRequestGatheringStarted]:
          transformPhysicalSettlementChanged,
      };

      const items = result.value?.data?.items as PositionItem[];

      if (actions[signal.code] && items?.length) {
        const data = actions[signal.code](
          signal.data as TransformTradePriceChangeDataType &
            TransformSymbolTradingStateChangeDataType
        );

        if (signal.code === CODES.OptionPositionCreated) {
          //create new row

          await getOptionPositions();

          return;
        }

        const index = items.findIndex((s) => s.isin === data.isin);

        if (index > -1) {
          const item = items[index];

          if (signal.code === CODES.SymbolTradePriceChangeSignal) {
            if (
              data.isin === item.underlyingAssetIsin &&
              item.underlyingAssetLastTradePrice !== data.lastTradePrice
            ) {
              Object.assign(
                item,
                getPositionSymbolValues(
                  {
                    ...item,
                    underlyingAssetLastTradePrice: data.lastTradePrice,
                    referencePriceVariationPercentage:
                      data.referencePriceVariationPercentage,
                  },
                  findWageConfigBySymbol(item)
                )
              );
            } else if (
              data.isin === item.isin &&
              item.lastTradePrice !== data.lastTradePrice
            ) {
              Object.assign(
                item,
                getPositionSymbolValues(
                  {
                    ...item,
                    lastTradePrice: data.lastTradePrice,
                    referencePriceVariationPercentage:
                      data.referencePriceVariationPercentage,
                  },
                  findWageConfigBySymbol(item)
                )
              );
            }
          } else if (signal.code === CODES.SymbolStateChangeSignal) {
            Object.assign(item, data);
          } else if (signal.code === CODES.OptionPositionRemoved) {
            items.splice(index, 1);
          } else if (
            signal.code === CODES.OptionPositionChanged ||
            signal.code === CODES.CashSettlementRequestGatheringStarted ||
            signal.code === CODES.PhysicalSettlementRequestGatheringStarted
          ) {
            await getOptionPositions();

            return;
          }
        }
      }
    },
  });

  const optionPositions = computed(() => {
    result.value?.data?.items?.forEach((position) => {
      Object.assign(
        position,
        getPositionSymbolValues(
          {
            ...position,
          },
          findWageConfigBySymbol(position)
        )
      );
    });

    return (result.value?.data.items as TOptionPositions) || [];
  });

  const stats = computed(() => {
    const defaultStats = {
      sumValues: 0,
      sumReturnValues: 0,
      sumReturnValuesPercentage: 0,
      sumConstPrice: 0,
    };

    if (!optionPositions.value?.length) {
      return defaultStats;
    }

    const { sumValues, sumReturnValues, sumConstPrice } =
      optionPositions.value.reduce(
        (total, symbol) => {
          total.sumConstPrice += symbol?.costPrice;
          total.sumReturnValues += symbol.returnValue;
          total.sumValues += symbol.value;

          return total;
        },
        { sumValues: 0, sumReturnValues: 0, sumConstPrice: 0 }
      );

    return {
      sumValues,
      sumReturnValues,
      sumReturnValuesPercentage: calculateOpenPositionReturnValuePersentage(
        sumReturnValues,
        sumConstPrice
      ),
      sumConstPrice,
    };
  });

  return {
    stats,
    optionPositions,
    getOptionPositionsStatus,
    getOptionPositions,
    isLoadingOptionPositions,
    isSuccessLoadingOptionPositions,
    isErrorLoadingOptionPositions,
  };
}
